"use client"

import { useTranslations } from "@/lib/intl/use-translations"
import { usePricingV4Copy } from "./pricing-v4-copy"

/**
 * Pricing v5's words, for all its drafts (builder, store, check-ups). What
 * v4 already says (the plan rhythms, the inclusions, the MRI cards, the
 * single-test tables) comes from v4's hook, so the drafts cannot drift. The
 * `pricingV5` namespace holds only what is new: the hero, the three steps,
 * the plan table, the Add buttons, the selection and its controls, the store
 * and the check-up cards. The bar's short button on a phone is the navbar's
 * "Book check-up".
 */
export function usePricingV5Copy() {
  const v4 = usePricingV4Copy()
  const v4t = useTranslations("pricingV4")
  const t = useTranslations("pricingV5")
  const nav = useTranslations("nav")
  const pricing = useTranslations("pricing")
  const ui = useTranslations("ui")

  return {
    HERO: {
      eyebrow: v4.HERO.eyebrow,
      plain: t("heroHeadingPlain"),
      accent: t("heroHeadingAccent"),
      body: t("heroBody"),
    },
    // The heading says how the pricing works, in one colour; the plan card
    // under it says to start with the plan (Eugene, 30.09).
    INTRO: {
      eyebrow: v4.HERO.eyebrow,
      heading: t("introHeading"),
      body: (price: string) => t("introBody", { price }),
    },
    HOW: { heading: nav("howItWorks") },
    STEPS: [1, 2, 3].map((n) => ({
      title: t(`step${n}Title` as "step1Title"),
      body: t(`step${n}Body` as "step1Body"),
    })),
    PLAN: {
      ...v4.PLAN,
      heading: t("planHeading"),
      cardHeading: t("planCardHeading"),
      cardLede: t("planCardLede"),
      // The plan's promises and lines are the membership page's, checked
      // against what it says (Eugene, 30.09: "take the real benefits from
      // the membership page"). They replace v4's, whose "prevention team
      // you can message all year" is not what the plan gives. The card shows
      // the first five lines, the popup all six.
      promises: [1, 2, 3].map((n) => t(`planPromise${n}` as "planPromise1")),
      included: [1, 2, 3, 4, 5, 6].map((n) =>
        t(`planBenefit${n}` as "planBenefit1")
      ),
      colRhythm: t("planColRhythm"),
      colPrice: t("planColPrice"),
      alwaysIn: t("planAlwaysIn"),
      samePrice: t("planSamePrice"),
      meta: t("planMeta"),
      billingShort: t("planBillingShort"),
      oneTimeShort: t("planOneTimeShort"),
      // Only what the membership terms and the booking say today: cancel
      // any time, a reminder a month before renewal. A refund before the
      // first test is open (spec Q5, Dennis and Anna).
      cancelNote: t("planCancelNote"),
      // The card's price as the homepage sets it, the figure and "/year",
      // the other two rhythms in one small line under it (Eugene, 30.09).
      perYearShort: t("perYearShort"),
      sheetName: t("planSheetName"),
      oneOff: t("planOneOff"),
      moreOften: (twice: string, four: string) =>
        t("planMoreOften", { twice, four }),
    },
    // Each package's one line is the booking's (addOnsStep.mriRowDescription*):
    // today's pricing popups open Advanced on Core, which the plan flow no
    // longer sells.
    MRI: {
      ...v4.MRI,
      packages: v4.MRI.packages.map((pkg) => ({
        ...pkg,
        desc: t(
          pkg.id === "core"
            ? "mriDescCore"
            : pkg.id === "advanced"
              ? "mriDescAdvanced"
              : "mriDescPro"
        ),
        // The card's list. Its promise row says the video call now, so the
        // full-body MRI's own call line leaves the card; the popup keeps it.
        cardBullets: pkg.bullets.filter((line) => line !== v4t("mriAdvanced4")),
      })),
      // The third promise on the MRI cards, the homepage card's words for
      // it with the MRI on (home-v4-copy.ts `doctorCall`).
      promiseCall: t("mriPromiseCall"),
      // "one time", after a scan's price on top of the plan.
      oneTime: t("mriOneTime"),
      // The card's button where there is no builder (the MRI test page).
      book: t("mriBook"),
      // The MRI test page's section line: what the first package adds to
      // the plan (Eugene, 30.09: "+1'990 to your Baseline plan").
      pageSub: (price: string) => t("mriPageSub", { price }),
      // The scan itself, by name, at the head of what an MRI's price holds,
      // so the list does not read as the plan alone (Eugene, 09.10).
      benefit: (id: string, name: string) =>
        t(
          id === "core"
            ? "mriBenefitCore"
            : id === "advanced"
              ? "mriBenefitAdvanced"
              : "mriBenefitPro",
          {
            name,
          }
        ),
      // The test page hero: the member price and its (i) pop-up, member
      // and one-off side by side (Eugene, 30.09, after Function's page).
      heroMembers: t("mriHeroMembers"),
      // A scan the full-body MRI holds, on its own page (Eugene, 01.10).
      scanIncluded: t("mriScanIncluded"),
      priceWithPlan: t("mriPriceWithPlan"),
      priceOneOff: t("mriPriceOneOff"),
      priceTotal: t("mriPriceTotal"),
      // The choice popup's receipt: the one-time total, and the blood
      // test's figure in the one-time offer, part of its price.
      totalOnce: t("mriTotalOnce"),
      included: t("mriIncluded"),
      priceInfo: t("mriPriceInfo"),
      chooseTitle: t("mriChooseTitle"),
      bookWithPlan: t("mriBookWithPlan"),
      optionPlanDesc: t("mriOptionPlanDesc"),
      optionOneOffDesc: t("mriOptionOneOffDesc"),
    },
    ADDONS: {
      ...v4.ADDONS,
      // The blood tests named the way the scans are, "More scans with
      // your MRI", with their own line under it (Eugene, 01.10).
      groups: v4.ADDONS.groups.map((group, i) =>
        i === 0 ? { ...group, label: t("addonsBloodLabel") } : group
      ),
      bloodSameDraw: t("addonsBloodSameDraw"),
      withoutPlan: (price: string) => t("withoutPlan", { price }),
      needsMri: t("needsMri"),
      doctorNote: t("doctorNote"),
      inBooking: t("addonsInBooking"),
      // Over the price column: the bold plan price and the struck-through
      // one-off price beside it (Eugene, 30.09).
      pricesHeader: t("addonsPricesHeader"),
      oneOff: t("addonsOneOff"),
      imagingNote: t("addonsImagingNote"),
      imagingDoctor: t("addonsImagingDoctor"),
      imagingSameVisit: t("addonsImagingSameVisit"),
      // The blood test page's list of the tests that go on top.
      bloodMore: t("addonsBloodMore"),
      // Under the folded list on the test pages: "View 4 more".
      viewMore: (count: number) =>
        t("addonsViewMore", { count: String(count) }),
      // The footnote to the "*" on the member prices (Eugene, 01.10).
      membersNote: t("mriHeroMembers"),
    },
    PICK: {
      add: t("add"),
      added: t("added"),
      saveTip: t("saveTip"),
      savedTip: t("savedTip"),
    },
    // The popups' and the "More info" pills' words, as in the booking.
    INFO: {
      more: pricing("moreInfo"),
      learnMore: ui("learnMore"),
      close: t("close"),
      withoutPlan: t("priceWithoutPlan"),
      needsMri: t("storeNeedsMri"),
      notAvailable: ui("notAvailable"),
      comingSoon: ui("comingSoon"),
    },
    SUMMARY: {
      title: t("summaryTitle"),
      savedTitle: t("savedTitle"),
      planLine: t("summaryPlanLine"),
      alwaysIn: t("planAlwaysIn"),
      renewal: (price: string) => t("summaryRenewal", { price }),
      cta: t("summaryCta"),
      ctaShort: nav("bookCheckup"),
      details: t("summaryDetails"),
      hideDetails: t("summaryHideDetails"),
      empty: t("summaryEmpty"),
      total: t("total"),
      remove: (name: string) => t("removeItem", { name }),
      addons: (count: number) => t("summaryAddons", { count }),
      clearAll: t("clearAll"),
      cleared: t("cleared"),
      undo: t("undo"),
    },
    // The blood test page's hero price on the membership-first site: the
    // plan a year, and the same choice popup as the MRI's (Eugene, 01.10).
    BLOOD: {
      planPrices: t("bloodPlanPrices"),
      oneOffDesc: t("bloodOptionOneOffDesc"),
    },
    CLOSE: {
      lede: t("closeLede"),
      // A scan page's results line: the MRI's results take longer than the
      // blood test's (audit, 09.10).
      mriResults: t("mriCloseResults"),
    },
  }
}
